import { createRouter, createWebHistory } from '@ionic/vue-router';
import { RouteRecordRaw } from 'vue-router';
import AppShell from '../views/AppShell.vue';
import { waitForAuthReady } from '@/composables/useAuth';
import { capturePageview } from '@/posthog';
import { trackUmamiPageview } from '@/umami';
import { trackFirebaseScreenView } from '@/firebaseAnalytics';
import { isAuthenticated } from '@/services/api';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginPage.vue'),
    meta: { public: true },
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/AuthCallbackPage.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: AppShell,
    children: [
      {
        path: '',
        redirect: '/record',
      },
      {
        path: 'record',
        name: 'record',
        component: () => import('@/views/RecordPage.vue'),
      },
      {
        path: 'flights',
        name: 'flights',
        component: () => import('@/views/FlightsPage.vue'),
      },
      {
        path: 'flight-edit/:flightId',
        name: 'flight-edit',
        component: () => import('@/views/FlightLogEditPage.vue'),
      },
      {
        path: 'flight-deck/:flightId?',
        name: 'flight-deck',
        component: () => import('@/views/FlightDeckPage.vue'),
      },
      {
        path: 'replay',
        name: 'replay',
        redirect: (to) => {
          const flight = to.query.flight;
          if (typeof flight === 'string' && flight.length > 0) {
            return { name: 'flight-deck', params: { flightId: flight } };
          }
          return { name: 'flights' };
        },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/ProfilePage.vue'),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Auth navigation guard + flight-deck empty-state redirect
router.beforeEach(async (to, _from, next) => {
  // Wait for the auth service to finish restoring/validating the session
  // so the guard never makes decisions on stale or un-validated localStorage data.
  await waitForAuthReady();

  const isPublic = to.meta.public === true;
  const hasValidSession = isAuthenticated();

  if (!isPublic && !hasValidSession) {
    next({ name: 'login' });
  } else if (to.name === 'login' && hasValidSession) {
    next({ path: '/record' });
  } else if (to.name === 'flight-deck' && !to.params.flightId) {
    next({ name: 'flights' });
  } else {
    next();
  }
});

const ROUTE_TITLES: Record<string, string> = {
  login: 'Sign In',
  record: 'Record',
  flights: 'My Flights',
  'flight-edit': 'Flight Details',
  'flight-deck': 'Replay',
  profile: 'Profile',
};

router.afterEach((to) => {
  const name = typeof to.name === 'string' ? to.name : '';
  const subtitle = ROUTE_TITLES[name];
  document.title = subtitle ? `SoFly — ${subtitle}` : 'SoFly';
  capturePageview(to);
  trackUmamiPageview(to);
  trackFirebaseScreenView(to);
});

export default router;
